import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  useMedications,
  useAddMedication,
  useDeleteMedication,
  useDeclareNoMedications,
  type Medication,
  type MedicationInput,
} from '@pelvia/shared/hooks/patient/useMedications'
import { suggestionsForConditions, type MedSuggestion } from '@pelvia/shared/lib/patient/medSuggestions'

const SPRING = { type: 'spring' as const, stiffness: 320, damping: 28 }

export default function MedicationsCard({ conditions }: { conditions?: readonly string[] }) {
  const { data, isLoading } = useMedications()
  const addMut = useAddMedication()
  const delMut = useDeleteMedication()
  const noneMut = useDeclareNoMedications()

  const [adding, setAdding] = useState(false)
  const meds = data?.medications ?? []
  const declaredNone = data?.no_medications_declared ?? false

  const handleAdd = async (input: MedicationInput) => {
    await addMut.mutateAsync(input)
    setAdding(false)
  }

  const handleDelete = (id: number) => {
    delMut.mutate(id)
  }

  return (
    <section className="card profile-meds-card" id="medications">
      <div className="card-header">
        <span className="card-title">Medications</span>
        <AnimatePresence initial={false} mode="wait">
          {!adding && (
            <motion.button
              key="add-btn"
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setAdding(true)}
              whileTap={{ scale: 0.94 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15 }}
            >
              + Add medication
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence initial={false}>
        {adding && (
          <motion.div
            key="med-form"
            className="med-add-form"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{
              height: { type: 'spring', stiffness: 280, damping: 30 },
              opacity: { duration: 0.18 },
              marginTop: { duration: 0.2 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <MedicationForm
              conditions={conditions}
              onCancel={() => setAdding(false)}
              onSubmit={handleAdd}
              saving={addMut.isPending}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {isLoading ? (
        <p className="med-empty">Loading…</p>
      ) : meds.length === 0 && !adding ? (
        declaredNone ? (
          <p className="med-empty">
            You’ve told us you don’t take any medications.{' '}
            <button
              type="button"
              className="med-none-undo"
              onClick={() => noneMut.mutate(false)}
              disabled={noneMut.isPending}
            >
              Undo
            </button>
          </p>
        ) : (
          <div className="med-empty">
            <p>
              No medications yet. Tap <strong>+ Add medication</strong> above to log one.
            </p>
            <button
              type="button"
              className="med-none-btn"
              onClick={() => noneMut.mutate(true)}
              disabled={noneMut.isPending}
            >
              I don’t take any medications
            </button>
          </div>
        )
      ) : (
        <motion.ul className="med-list" layout>
          <AnimatePresence initial={false}>
            {meds.map((m) => (
              <motion.li
                key={m.id}
                className="med-row"
                layout
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, height: 0, marginTop: 0, marginBottom: 0 }}
                transition={SPRING}
              >
                <MedRow med={m} onDelete={() => handleDelete(m.id)} />
              </motion.li>
            ))}
          </AnimatePresence>
        </motion.ul>
      )}
    </section>
  )
}

function MedRow({ med, onDelete }: { med: Medication; onDelete: () => void }) {
  const detail = [med.dosage, med.frequency].filter(Boolean).join(' · ')
  return (
    <>
      <div className="med-row-body">
        <div className="med-row-name">{med.name}</div>
        {detail && <div className="med-row-detail">{detail}</div>}
        {med.as_needed && <span className="med-row-pill">as needed</span>}
      </div>
      <motion.button
        type="button"
        className="med-row-delete"
        onClick={onDelete}
        whileTap={{ scale: 0.9 }}
        aria-label={`Remove ${med.name}`}
      >
        ×
      </motion.button>
    </>
  )
}

function MedicationForm({
  conditions,
  onCancel,
  onSubmit,
  saving,
}: {
  conditions?: readonly string[]
  onCancel: () => void
  onSubmit: (input: MedicationInput) => void | Promise<void>
  saving: boolean
}) {
  const [name, setName] = useState('')
  const [dosage, setDosage] = useState('')
  const [frequency, setFrequency] = useState('')
  const [asNeeded, setAsNeeded] = useState(false)
  const [regimenStart, setRegimenStart] = useState('')

  const suggestions = useMemo(() => suggestionsForConditions(conditions), [conditions])

  const applySuggestion = (s: MedSuggestion) => {
    setName(s.name)
    setDosage(s.dosage || '')
    setFrequency(s.frequency || '')
    setAsNeeded((s.frequency || '').toLowerCase().includes('as needed'))
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSubmit({
      name: name.trim(),
      dosage: dosage.trim() || null,
      frequency: frequency.trim() || null,
      as_needed: asNeeded,
      regimen_start_date: regimenStart || null,
    })
  }

  return (
    <form onSubmit={submit} className="med-form-grid">
      {suggestions.length > 0 && (
        <div className="med-suggestions">
          <div className="med-suggestions-label">
            Common for your conditions{' '}
            <span className="form-hint">tap to prefill</span>
          </div>
          <div className="med-suggestions-row">
            {suggestions.map((s) => (
              <motion.button
                key={s.name}
                type="button"
                className={`med-suggestion${name === s.name ? ' active' : ''}`}
                onClick={() => applySuggestion(s)}
                whileTap={{ scale: 0.94 }}
                title={s.category}
              >
                <span className="med-suggestion-name">{s.name}</span>
                {s.dosage && <span className="med-suggestion-dose">{s.dosage}</span>}
              </motion.button>
            ))}
          </div>
        </div>
      )}
      <div className="form-field">
        <label className="form-label">Name</label>
        <input
          className="form-input"
          placeholder="e.g. Ibuprofen"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>
      <div className="med-form-row">
        <div className="form-field">
          <label className="form-label">Dosage</label>
          <input
            className="form-input"
            placeholder="e.g. 400mg"
            value={dosage}
            onChange={(e) => setDosage(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Frequency</label>
          <input
            className="form-input"
            placeholder="e.g. 2x daily"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          />
        </div>
      </div>
      <label className="med-form-checkbox">
        <input
          type="checkbox"
          checked={asNeeded}
          onChange={(e) => setAsNeeded(e.target.checked)}
        />
        <span>Take only as needed</span>
      </label>
      <div className="form-field">
        <label className="form-label">
          Hormonal therapy start date <span className="form-hint">optional</span>
        </label>
        <input
          className="form-input"
          type="date"
          value={regimenStart}
          onChange={(e) => setRegimenStart(e.target.value)}
        />
        <span className="form-hint">
          For cyclic birth control (e.g. 21/7 pill), the pack start date sharpens
          your withdrawal-bleed estimate.
        </span>
      </div>
      <div className="med-form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <motion.button
          type="submit"
          className="btn btn-primary"
          disabled={saving || !name.trim()}
          whileTap={{ scale: 0.96 }}
        >
          {saving ? 'Adding…' : 'Add medication'}
        </motion.button>
      </div>
    </form>
  )
}
